import { Relation, Ty } from '@cotera/era';
import { Table } from 'apache-arrow';
import * as duckdb from '@duckdb/duckdb-wasm';
import { v4 } from 'uuid';
import { DuckDBQueryResult } from '../duckdb';
import { z } from 'zod/v3';
import { Pool, Options as PoolOptions, createPool } from 'generic-pool';
import { Disposable, using } from '@cotera/utilities';

const summarizeItem = z.object({
  column_name: z.string(),
  column_type: z.string(),
  min: z.string().nullable(),
  max: z.string().nullable(),
  approx_unique: z.number({ coerce: true }).nullable(),
  avg: z.number({ coerce: true }).nullable(),
  std: z.number({ coerce: true }).nullable(),
});

const summarizeSchema = z.array(summarizeItem);

export type SummarizeRecord = z.infer<typeof summarizeItem>;

declare global {
  interface Window {
    DUCKDB_MEMORY_LIMIT_IN_MB: number;
    DUCKDB_MEMORY_RELEASE_THRESHOLD: number;
  }
}

window.DUCKDB_MEMORY_LIMIT_IN_MB = 1024;
window.DUCKDB_MEMORY_RELEASE_THRESHOLD = 0.6;

type Connection = {
  query: duckdb.AsyncDuckDBConnection['query'];
  prepare: (sql: string) => Promise<{
    query: (...params: any[]) => Promise<Table<any>>;
  }>;
  close: duckdb.AsyncDuckDBConnection['close'];
};
type DbManager = {
  connect: () => Promise<Connection>;
};

/**
 * `PRAGMA database_size` reports memory as a human string — `"512.0MB"`,
 * `"1.2GB"` — so the unit has to be read before the number means anything.
 *
 * Null when the value cannot be read as a size at all, which callers must treat
 * as "no opinion" rather than as a large number: the pragma's shape is DuckDB's
 * to change, and a parse that silently produced `NaN` used to leave every
 * comparison against it false.
 */
const memoryUsageInMb = (raw: unknown): number | null => {
  if (typeof raw !== 'string') {
    return null;
  }
  const match = /^\s*([\d.]+)\s*(B|KB|MB|GB|TB)?\s*$/i.exec(raw);
  const magnitude = match?.[1];
  if (magnitude === undefined) {
    return null;
  }
  const value = Number(magnitude);
  if (!Number.isFinite(value)) {
    return null;
  }
  switch (match?.[2]?.toUpperCase()) {
    case 'B':
      return value / (1024 * 1024);
    case 'KB':
      return value / 1024;
    case 'GB':
      return value * 1024;
    case 'TB':
      return value * 1024 * 1024;
    // A bare number, and `MB`, are both already in megabytes.
    default:
      return value;
  }
};

export class DuckDBPool {
  private pool: Pool<PooledConnection>;

  constructor(private readonly internal: DbManager, poolOptions: PoolOptions) {
    this.pool = createPool(
      {
        create: async () => {
          return await this.connect();
        },
        destroy: async (connection: PooledConnection) => {
          await connection.connection.close();
        },
        // Runs on every borrow (`testOnBorrow`), so it sits in front of every
        // query in the tab and has exactly one job: say whether this connection
        // is still cheap to reuse. It must never throw and must never guess
        // "invalid" — `generic-pool` does not catch a rejecting validator, and a
        // rejection leaves the resource neither available nor loaned while the
        // waiting caller is never dispatched. Six of those and `acquire()` stops
        // resolving for the rest of the session: every query after it hangs, with
        // nothing thrown and nothing logged.
        validate: async (connection: PooledConnection) => {
          try {
            const dbInfo = await connection.connection.query(
              `PRAGMA database_size;`
            );
            const megabytes = memoryUsageInMb(
              new DuckDBQueryResult(dbInfo, {
                memory_usage: Ty.ty('int'),
              })
                .column('memory_usage')
                .rawValueAt(0)
            );
            // Unreadable is not the same as too large. Recycling here would
            // close the connection and open another — which re-runs `INSTALL
            // json; LOAD json;` over the network — on every single borrow.
            if (megabytes === null) {
              return true;
            }
            // Under the release threshold the connection is still worth reusing;
            // over it, it is retired so its memory goes back.
            return (
              megabytes <
              window.DUCKDB_MEMORY_LIMIT_IN_MB *
                window.DUCKDB_MEMORY_RELEASE_THRESHOLD
            );
          } catch {
            return true;
          }
        },
      },
      poolOptions
    );
  }

  // Connect method to establish a new connection
  private async connect(): Promise<PooledConnection> {
    return await this.internal.connect().then(async (c) => {
      await c.query(`INSTALL json; LOAD json;`);
      await c.query(`
        SET memory_limit = '${window.DUCKDB_MEMORY_LIMIT_IN_MB}MB';
        SET temp_directory='/cotera/db/tmp.tmp';
      `);
      return new PooledConnection(this, c);
    });
  }

  // Get a connection from the pool
  public async aquire(): Promise<PooledConnection> {
    return await this.pool.acquire();
  }

  // Release a connection back to the pool
  public async release(connection: PooledConnection): Promise<void> {
    await this.pool.release(connection);
  }

  // Drains and shuts down the pool
  public async closePool(): Promise<void> {
    await this.pool.drain();
    await this.pool.clear();
  }
}

export class PooledConnection implements Disposable {
  constructor(
    private readonly pool: DuckDBPool,
    private readonly _connection: Connection
  ) {}

  async dispose() {
    await this.pool.release(this);
  }

  get connection() {
    return this._connection;
  }
}

declare global {
  interface Window {
    DUCKDB_ADD_ON_QUERY_START_HOOK: (
      cb: (sql: string, params: Record<string, any>) => void
    ) => () => void;
  }
}

let ON_START_QUERY_HOOKS: {
  hook: (sql: string, params: Record<string, any>) => void;
  token: string;
}[] = [];

window.DUCKDB_ADD_ON_QUERY_START_HOOK = (hook) => {
  const token = v4();
  ON_START_QUERY_HOOKS.push({ hook, token });
  return () => {
    ON_START_QUERY_HOOKS = ON_START_QUERY_HOOKS.filter(
      (hook) => hook.token !== token
    );
  };
};

export class CoteraDuckDB {
  private readonly registeredFiles: Set<string> = new Set();

  constructor(
    private readonly internal: {
      registerFileBuffer: (name: string, buffer: Uint8Array) => Promise<void>;
      registerFileURL: (
        name: string,
        url: string,
        proto: duckdb.DuckDBDataProtocol,
        directIO: boolean
      ) => Promise<void>;
      dropFile: (name: string) => Promise<unknown>;
    },
    private readonly pool: DuckDBPool
  ) {}

  async rawQuery(sql: string) {
    return await using(await this.pool.aquire(), async ({ connection }) => {
      for (const { hook } of ON_START_QUERY_HOOKS) {
        hook(sql, {});
      }
      const stmt = await connection.prepare(sql);
      const res = await stmt.query();

      return res;
    });
  }

  async query(rel: Relation): Promise<Table> {
    return await using(await this.pool.aquire(), async ({ connection }) => {
      const { sql, params } = rel.duckdbwasmSql();
      for (const { hook } of ON_START_QUERY_HOOKS) {
        hook(sql, params);
      }
      const stmt = await connection.prepare(sql);
      const res = await stmt.query(...params);

      return res;
    });
  }

  async summarize(rel: Relation): Promise<DuckDBQueryResult<SummarizeRecord>> {
    return await using(await this.pool.aquire(), async ({ connection }) => {
      const { sql, params } = rel.duckdbwasmSql();
      const summarizeSql = `SUMMARIZE ${sql}`;

      for (const { hook } of ON_START_QUERY_HOOKS) {
        hook(summarizeSql, params);
      }

      const stmt = await connection.prepare(summarizeSql);
      const res = await stmt.query(...params);

      return new DuckDBQueryResult(
        res,
        {
          column_name: Ty.ty('string'),
          column_type: Ty.ty('string'),
          min: Ty.ty('string'),
          max: Ty.ty('string'),
          approx_unique: Ty.ty('int'),
          avg: Ty.ty('float'),
          std: Ty.ty('float'),
        },
        summarizeSchema
      );
    });
  }

  /**
   * Registers a file under a caller-chosen name, replacing whatever was
   * registered under it before.
   *
   * `registerFileBuffer` refuses a name it already holds, so the old
   * registration has to be dropped first — which is also what frees the
   * previous buffer. That is the point: a caller that reloads the same logical
   * file on a timer would otherwise accumulate one buffer in the wasm
   * filesystem per reload, for the lifetime of the tab.
   *
   * Deliberately name-per-caller rather than minting one: the predecessor here
   * generated a uuid every call and nothing ever dropped it, so every reload
   * leaked a buffer into the wasm filesystem for the lifetime of the tab.
   */
  async replaceFile(name: string, file: File): Promise<string> {
    if (this.registeredFiles.has(name)) {
      await this.internal.dropFile(name);
      this.registeredFiles.delete(name);
    }
    await this.registerFileBuffer(
      name,
      new Uint8Array(await file.arrayBuffer())
    );

    return name;
  }

  /**
   * Points a name at a URL, dropping whatever it held before.
   *
   * The counterpart to {@link registerFileURL} for the one case its name-cache
   * is wrong: a URL that has stopped answering. A parquet's signed URL 403s once
   * its signature expires, and a dataset being actively written prunes the
   * artifact a lagging reader still holds — in both the bytes behind the name are
   * gone, and `registerFileURL` would keep serving the dead URL because the name
   * is already registered. Re-resolving the URL upstream only helps if the new
   * one actually replaces the old here, which is what this does.
   */
  async replaceFileURL(name: string, url: string) {
    if (this.registeredFiles.has(name)) {
      await this.internal.dropFile(name);
      this.registeredFiles.delete(name);
    }
    await this.registerFileURL(name, url);
  }

  async registerFileURL(name: string, url: string) {
    if (this.registeredFiles.has(name)) {
      return;
    }

    try {
      await this.internal.registerFileURL(
        name,
        url,
        duckdb.DuckDBDataProtocol.HTTP,
        false
      );
      this.registeredFiles.add(name);
    } catch (e) {
      if (
        e instanceof Error &&
        e.message.toLocaleLowerCase().includes('file already registered')
      ) {
        this.registeredFiles.add(name);
        return;
      }
      throw e;
    }
  }

  private async registerFileBuffer(name: string, buffer: Uint8Array) {
    if (this.registeredFiles.has(name)) {
      return;
    }

    try {
      await this.internal.registerFileBuffer(name, buffer);
      this.registeredFiles.add(name);
    } catch (e) {
      if (
        e instanceof Error &&
        e.message.toLocaleLowerCase().includes('file already registered')
      ) {
        this.registeredFiles.add(name);
        return;
      }
      throw e;
    }
  }
}
